import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { TrendingUp, ArrowRight } from "lucide-react";

/**
 * Real-time mini dashboard shown on the homepage.
 * Displays how many candidates are currently at each pipeline stage,
 * separated by Non-Formal and Formal tracks. Powers transparency for
 * prospective employers ("volume visible before contact").
 */
export default function StageProgressMini() {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ["pipeline-stats-mini"],
    queryFn: async () => (await api.get("/pipeline/stats")).data,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  if (!data || (data.non_formal?.total === 0 && data.formal?.total === 0)) return null;

  const sections = [
    { key: "non_formal", label: t("home.pipeline_non_formal", "Non-Formal Pipeline"), sub: t("home.pipeline_non_formal_sub", "Domestic Workers"), badgeColor: "bg-zinc-100 text-zinc-900 border-zinc-300" },
    { key: "formal",     label: t("home.pipeline_formal",     "Formal Pipeline"),     sub: t("home.pipeline_formal_sub",     "Professionals"),  badgeColor: "bg-emerald-100 text-emerald-900 border-emerald-300" },
  ];

  return (
    <section className="py-14 sm:py-20 px-5 sm:px-6 lg:px-10 bg-white" data-testid="home-stage-progress">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
          <div>
            <div className="overline text-[#8a5f07] mb-2 flex items-center gap-2 text-[10px] sm:text-xs">
              <TrendingUp size={13} /> {t("home.pipeline_overline", "Live Pipeline · Real-time volume")}
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-zinc-950 tracking-tight max-w-2xl leading-tight">
              {t("home.pipeline_headline", "Candidates currently in process")}
            </h2>
            <p className="text-sm sm:text-base text-zinc-700 mt-3 max-w-2xl leading-relaxed">
              {t("home.pipeline_subhead", "Full transparency on every candidate we're actively placing — split across Non-Formal (domestic) and Formal (professional) tracks.")}
            </p>
          </div>
          <Link
            to="/pipeline"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-zinc-950 text-[13px] font-semibold hover:bg-zinc-950 hover:text-white transition-all"
            data-testid="home-stage-view-all"
          >
            {t("home.pipeline_cta", "View full pipeline")} <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {sections.map((sec) => {
            const s = data[sec.key] || { stages: [], counts: {}, total: 0 };
            return (
              <div
                key={sec.key}
                className={`rounded-3xl p-6 md:p-8 border ${sec.key === "formal" ? "bg-gradient-to-br from-emerald-950 to-emerald-900 text-emerald-50 border-emerald-800" : "bg-gradient-to-br from-zinc-950 to-zinc-900 text-zinc-100 border-zinc-800"} relative overflow-hidden`}
                data-testid={`home-stage-panel-${sec.key}`}
              >
                {/* Soft gold radial glow */}
                <div
                  className="absolute -top-16 -right-16 w-48 h-48 rounded-full blur-3xl opacity-20 pointer-events-none"
                  style={{ background: "radial-gradient(circle, #d4a017 0%, transparent 65%)" }}
                />
                <div className="relative">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.24em] opacity-70 font-semibold">{sec.label}</div>
                      <div className="text-[12px] opacity-60 mt-0.5">{sec.sub}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-serif text-4xl md:text-5xl text-[#f5d78a] tabular-nums leading-none">{s.total}</div>
                      <div className="text-[10px] uppercase tracking-widest opacity-70 mt-1">candidates</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(s.stages || []).map((stg, i) => {
                      const cnt = (s.counts || {})[stg] || 0;
                      const dim = cnt === 0 ? "opacity-40" : "";
                      return (
                        <div
                          key={stg}
                          className={`px-3 py-1.5 rounded-full text-[11px] border border-white/15 bg-white/5 inline-flex items-center gap-2 ${dim}`}
                          data-testid={`home-stage-chip-${sec.key}-${stg}`}
                          title={`Step ${i + 1}`}
                        >
                          <span className="capitalize">{stg.replace(/_/g, " ")}</span>
                          <span className="font-bold tabular-nums text-[#f5d78a]">{cnt}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
